import { createContext, useContext, useEffect, type ReactNode } from "react"
import { useInView } from "react-intersection-observer"

import cloudflareLoader from "@/lib/cloudflare-loader"
import { useAppSelector } from "@/lib/storage/hooks"
import { selectIsUserCrawler } from "@/lib/storage/user-slice"

// `loading="lazy"` only defers images a few thousand pixels below the fold, which on a
// shorter page is every image on it. This is the gate that actually holds one back, and
// it splits the question in two: an observer intersects its target with every ancestor's
// clip, so a card scrolled off the side of its row never intersects the viewport however
// large the margin. The row answers "am I on my way in" once for all of its photos; each
// photo answers "am I near the visible part of the row", with ~3 cards of lookahead.
const DEFER_ROW_MARGIN = "0px 600px"
const DEFER_ROW_VIEWPORT_MARGIN = "400px 0px"

// Small enough to weigh about 100 bytes, blurred enough that the upscale reads as
// deliberate rather than broken.
const PREVIEW_WIDTH = 24
const PREVIEW_QUALITY = 30
const PREVIEW_BLUR = 20

interface PhotoScrollRootValue {
  element: HTMLElement | null
  /** Whether the row itself is near the viewport — watched once here, not per photo. */
  nearViewport: boolean
}

const PhotoScrollRootContext = createContext<PhotoScrollRootValue | null>(null)

/**
 * Publishes the scrollable row its photos sit in, which is also what turns deferral on:
 * a photo inside one holds its rendition back, a photo anywhere else loads normally.
 * Carousels wrap their items in one.
 */
export function PhotoScrollRoot({
  element,
  children,
}: {
  element: HTMLElement | null
  children: ReactNode
}) {
  // A crawler never scrolls, so deferring would leave it nothing to index. Withholding
  // the row rather than flagging it keeps that exemption out of every photo.
  const isCrawler = useAppSelector(selectIsUserCrawler)
  const { ref, inView } = useInView({
    rootMargin: DEFER_ROW_VIEWPORT_MARGIN,
    triggerOnce: true,
    fallbackInView: true,
  })

  // The ref owns the observation, so release it here: nothing else unobserves an
  // element handed to it outside of JSX.
  useEffect(() => {
    ref(element)
    return () => ref(null)
  }, [ref, element])

  return (
    <PhotoScrollRootContext.Provider
      value={isCrawler ? null : { element, nearViewport: inView }}
    >
      {children}
    </PhotoScrollRootContext.Provider>
  )
}

/**
 * Decides when a photo may fetch its real rendition, and what to show until then.
 * Deferral applies to photos rendered inside a `PhotoScrollRoot`; `shouldLoad` false
 * means the caller shows the stand-in alone.
 */
export function usePhotoDeferral({
  src,
  priority,
}: {
  src: string
  priority?: boolean
}) {
  const scrollRoot = useContext(PhotoScrollRootContext)
  const gated = !!scrollRoot && !priority

  // `fallbackInView` loads straight away where there is no observer; a server render
  // reaches it at all, so its markup and the first client render agree and hydration
  // never swaps a src.
  const { ref: observerRef, inView: nearRow } = useInView({
    root: scrollRoot?.element ?? null,
    rootMargin: DEFER_ROW_MARGIN,
    triggerOnce: true,
    fallbackInView: true,
    skip: !gated,
    initialInView: !gated,
  })
  const nearEnough = nearRow && !!scrollRoot?.nearViewport

  return {
    shouldLoad: !gated || nearEnough,
    previewUrl: gated
      ? cloudflareLoader({
          src,
          width: PREVIEW_WIDTH,
          quality: PREVIEW_QUALITY,
          blur: PREVIEW_BLUR,
        })
      : undefined,
    observerRef,
  }
}
